import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2, Plus, Trash2, Users, Calendar } from 'lucide-react';

export function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', payout_method: 'paypal', paypal_email: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAdminAgents();
      setAgents(res.data || []);
    } catch {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('Email, password, and name are required');
      return;
    }
    setSaving(true);
    try {
      await api.createAdminAgent(form);
      toast.success('Agent created!');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', payout_method: 'paypal', paypal_email: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete agent "${name}"? Their lineups will be cleared and the user account demoted.`)) return;
    try {
      await api.deleteAdminAgent(id);
      toast.success('Agent deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete agent');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-lime" /> Agents
          </h2>
          <p className="text-sm text-[#A1A1AA]">Curate the Weekly Specials page. Agents are admin-created and stay private to buyers.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="add-agent-btn">
          <Plus className="w-4 h-4 mr-2" />Create Agent
        </Button>
      </div>

      {agents.length === 0 ? (
        <div className="text-center py-16 bg-surface rounded-2xl border border-[#262626]">
          <Users className="w-16 h-16 mx-auto mb-4 text-[#333]" />
          <h3 className="text-xl font-heading font-bold mb-2">No Agents Yet</h3>
          <p className="text-[#A1A1AA]">Create an agent so they can build a Weekly Specials lineup.</p>
        </div>
      ) : (
        <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden">
          <div className="divide-y divide-[#262626]">
            {agents.map((a) => (
              <div key={a.id} className="p-4 flex items-center justify-between gap-4" data-testid={`agent-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold truncate">{a.name}</h3>
                    <Badge className="bg-lime/10 text-lime border-0 text-xs">Agent</Badge>
                  </div>
                  <p className="text-xs text-[#A1A1AA] truncate">
                    {a.email} · ${a.available_balance?.toFixed?.(2) || '0.00'} available · {a.payout_method === 'paypal' ? `PayPal: ${a.paypal_email || '—'}` : a.payout_method}
                  </p>
                  <p className="text-xs text-[#666] flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    {a.current_week_lineup_count || 0} artists in this week&apos;s lineup
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={() => handleDelete(a.id, a.name)}
                  data-testid={`delete-agent-${a.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-[#262626]">
          <DialogHeader>
            <DialogTitle>Create Agent</DialogTitle>
            <DialogDescription>The agent will use these credentials to log in and manage Weekly Specials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" placeholder="e.g. Code Red Sound Agent" data-testid="agent-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" placeholder="agent@example.com" data-testid="agent-form-email" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" placeholder="Minimum 8 characters" data-testid="agent-form-password" />
            </div>
            <div className="space-y-2">
              <Label>PayPal Email (payout)</Label>
              <Input type="email" value={form.paypal_email} onChange={(e) => setForm({ ...form, paypal_email: e.target.value })} className="input-dark" placeholder="agent-paypal@example.com" data-testid="agent-form-paypal" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#262626]" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleCreate} disabled={saving} data-testid="agent-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
